import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Clock,
  Cpu,
  Loader2,
  AlertCircle,
  Download,
  Trash2,
  Users,
  Calendar,
} from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScoreBar, ScoreBadge } from '@/features/evaluation/components/ScoreBar';
import { CriteriaBreakdown } from '@/features/evaluation/components/CriteriaBreakdown';
import { PassageCitationList } from '@/features/evaluation/components/PassageCitation';
import { useEvaluation, useDeleteEvaluation } from '@/features/evaluation/hooks/useEvaluation';
import { AI_MODEL_LABELS, getBucketCategory, BUCKET_LABELS } from '@/features/evaluation/types';
import type { BucketCategory } from '@/features/evaluation/types';

// =========================================
// Results page — detailed evaluation view
// =========================================

const BUCKET_STYLES: Record<BucketCategory, string> = {
  include: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  maybe: 'bg-amber-100 text-amber-800 border-amber-200',
  exclude: 'bg-rose-100 text-rose-800 border-rose-200',
};

export function Results() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: evaluation, isLoading, error } = useEvaluation(id);
  const deleteEvaluation = useDeleteEvaluation();

  const handleDelete = async () => {
    if (!id) return;
    await deleteEvaluation.mutateAsync(id);
    navigate('/evaluate');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto" />
          <p className="text-sm text-slate-500">Loading evaluation results...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <AlertCircle className="h-8 w-8 text-rose-500 mx-auto" />
          <p className="text-sm text-slate-600">Failed to load evaluation</p>
          <p className="text-xs text-slate-400">{error.message}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/evaluate')}>
            Back to Evaluations
          </Button>
        </div>
      </div>
    );
  }

  // Not found
  if (!evaluation) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <FileText className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="text-sm text-slate-600">Evaluation not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/evaluate')}>
            Back to Evaluations
          </Button>
        </div>
      </div>
    );
  }

  const score = evaluation.score ?? 0;
  const bucket = getBucketCategory(score);
  const allPassages = evaluation.cited_passages ?? [];
  const criteriaNames: Record<string, string> = {};
  if (evaluation.breakdown) {
    for (const ws of evaluation.breakdown.weighted_scores) {
      criteriaNames[ws.criteria_id] = ws.criteria_name;
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              Evaluation Results
            </h1>
            {evaluation.source && (
              <p className="text-sm text-slate-500 truncate">
                {evaluation.source.title ?? evaluation.source.file_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {evaluation.source && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/chat/${evaluation.source.id}`}>
                    <FileText className="h-4 w-4 mr-1" />
                    Chat
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Chat with this source</TooltipContent>
            </Tooltip>
          )}
          <DeleteConfirmDialog onConfirm={handleDelete} />
        </div>
      </div>

      {/* Status banner for pending/processing/failed */}
      {evaluation.status !== 'completed' && (
        <Card className={cn(
          'border-l-4',
          evaluation.status === 'processing' && 'border-l-amber-400 bg-amber-50',
          evaluation.status === 'pending' && 'border-l-slate-400 bg-slate-50',
          evaluation.status === 'failed' && 'border-l-rose-400 bg-rose-50',
        )}>
          <CardContent className="py-4 flex items-center gap-3">
            {evaluation.status === 'processing' && (
              <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
            )}
            {evaluation.status === 'failed' && (
              <AlertCircle className="h-5 w-5 text-rose-500" />
            )}
            {evaluation.status === 'pending' && (
              <Clock className="h-5 w-5 text-slate-400" />
            )}
            <div>
              <p className="text-sm font-medium text-slate-700 capitalize">
                {evaluation.status}
              </p>
              <p className="text-xs text-slate-500">
                {evaluation.status === 'processing' && 'AI is analyzing the source...'}
                {evaluation.status === 'pending' && 'Evaluation is queued for processing'}
                {evaluation.status === 'failed' && 'Evaluation failed. Please try again.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Score hero card */}
      <Card>
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Score circle */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div
                className={cn(
                  'w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center border-4',
                  bucket === 'include' && 'border-emerald-400 bg-emerald-50',
                  bucket === 'maybe' && 'border-amber-400 bg-amber-50',
                  bucket === 'exclude' && 'border-rose-400 bg-rose-50',
                )}
              >
                <span
                  className={cn(
                    'text-2xl sm:text-3xl font-bold tabular-nums',
                    bucket === 'include' && 'text-emerald-700',
                    bucket === 'maybe' && 'text-amber-700',
                    bucket === 'exclude' && 'text-rose-700',
                  )}
                >
                  {score}
                </span>
              </div>
              <Badge className={cn('text-xs', BUCKET_STYLES[bucket])}>
                {BUCKET_LABELS[bucket]}
              </Badge>
            </div>

            {/* Score bar + metadata */}
            <div className="flex-1 space-y-4 min-w-0">
              <ScoreBar
                score={score}
                showPercentage
                showBucket={false}
                size="lg"
              />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetaItem
                  icon={Cpu}
                  label="Model"
                  value={AI_MODEL_LABELS[evaluation.ai_model]}
                />
                <MetaItem
                  icon={Calendar}
                  label="Date"
                  value={format(new Date(evaluation.created_at), 'MMM d, yyyy')}
                />
                <MetaItem
                  icon={Clock}
                  label="Status"
                  value={evaluation.status}
                />
                <MetaItem
                  icon={Download}
                  label="Credits"
                  value={`${evaluation.credits_used} used`}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Source info card */}
      {evaluation.source && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-500" />
              Source Document
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">
                    {evaluation.source.file_name}
                  </p>
                  {evaluation.source.title && (
                    <p className="text-sm text-slate-600 mt-0.5">
                      {evaluation.source.title}
                    </p>
                  )}
                  {evaluation.source.authors && evaluation.source.authors.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {evaluation.source.authors.join(', ')}
                    </p>
                  )}
                </div>
                <ScoreBadge score={score} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content: Breakdown + Passages */}
      {evaluation.status === 'completed' && evaluation.breakdown && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Criteria breakdown */}
          <CriteriaBreakdown
            evaluationId={evaluation.id}
            breakdown={evaluation.breakdown}
            criteriaResults={evaluation.criteria_results}
          />

          {/* Cited passages */}
          <PassageCitationList
            passages={allPassages}
            criteriaNames={criteriaNames}
            maxVisible={5}
          />
        </div>
      )}
    </div>
  );
}

// =========================================
// DeleteConfirmDialog — confirmation dialog
// =========================================

interface DeleteConfirmDialogProps {
  onConfirm: () => void;
}

function DeleteConfirmDialog({ onConfirm }: DeleteConfirmDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700">
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Evaluation?</DialogTitle>
          <DialogDescription>
            This will permanently delete this evaluation and all its results.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================
// MetaItem — small metadata display
// =========================================

interface MetaItemProps {
  icon: typeof Clock;
  label: string;
  value: string;
}

function MetaItem({ icon: Icon, label, value }: MetaItemProps) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-slate-700 font-medium truncate capitalize">{value}</p>
      </div>
    </div>
  );
}
